import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import TsAuthGate from "@/components/mobile/TsAuthGate";
import TsOnboarding from "@/components/mobile/TsOnboarding";
import TsEmployeeSignup from "@/components/mobile/TsEmployeeSignup";
import TsPendingApproval from "@/components/mobile/TsPendingApproval";
import { loginTsFirebaseWithStatus } from "@/lib/tsEmployeeApi";
import {
  completeTsOnboarding,
  consumeTsFreshLogin,
  applyTsPortalTokens,
  fetchTsBootstrap,
  finalizeTsDepartments,
  isDesignsOnlyIdentity,
  isRiderUseDaError,
  loadTsDepartments,
  loadTsIdentity,
  markTsFreshLogin,
  resumeTsSession,
  shouldShowTsOnboarding,
  tsAppPortionPaths,
} from "@/lib/tsIdentity";
import { deliveryJoinPath, staffBrandKicker, staffBrandTitle } from "@/lib/teamHub";

const PINK = "#EE2677";

function isPhoneVerificationExpired(ex) {
  const status = Number(ex?.status || 0);
  const msg = String(ex?.message || "");
  if (status === 401) return true;
  return /expired|invalid or expired|phone verification|try again/i.test(msg);
}

/**
 * Unified staff session gate — silent JWT resume, OTP when needed, onboarding after fresh OTP.
 */
export default function TsSessionGate({ onReady, children }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("checking");
  const [identity, setIdentity] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [onboardBusy, setOnboardBusy] = useState(false);
  const [err, setErr] = useState("");
  const [signupContext, setSignupContext] = useState(null);
  const [pendingEmployee, setPendingEmployee] = useState(null);
  const [pendingBusy, setPendingBusy] = useState(false);

  const finishReady = useCallback(
    (id, data) => {
      setIdentity(id);
      setSessionData(data || null);
      setPhase("ready");
      onReady?.({ identity: id, data });
    },
    [onReady]
  );

  const goNeedsOtp = useCallback((message = "") => {
    setSignupContext(null);
    setPendingEmployee(null);
    setErr(message || "");
    setPhase("needs_otp");
  }, []);

  const runResume = useCallback(async () => {
    setPhase("checking");
    setErr("");
    try {
      const result = await resumeTsSession();
      if (result.status === "rider_blocked") {
        setPhase("rider_blocked");
        return;
      }
      if (result.status === "needs_otp") {
        setPhase("needs_otp");
        return;
      }
      if (result.status === "needs_onboarding") {
        setIdentity(result.identity);
        setSessionData(result.data || null);
        setPhase("needs_onboarding");
        return;
      }
      await fetchTsBootstrap().catch(() => null);
      finishReady(result.identity, result.data);
    } catch (ex) {
      setErr(ex.message || "Could not restore session");
      setPhase("resume_error");
    }
  }, [finishReady]);

  useEffect(() => {
    runResume();
  }, [runResume]);

  const handleOtpComplete = (data) => {
    if (data?.status === "needs_signup") {
      // Unknown phone → staff access request (manager approves on Team panel).
      // Payable riders never reach here: login returns the Delivery-app 403 instead
      // (see gate_ts_login_permissions / isRiderUseDaError → rider CTA on TsAuthGate).
      setSignupContext({
        phone: data.phone || "",
        firebaseIdToken: data.firebaseIdToken || "",
      });
      setPhase("needs_signup");
      return;
    }
    if (data?.status === "pending_approval") {
      setPendingEmployee(data.employee || null);
      setSignupContext({
        phone: data.employee?.phone || data.phone || "",
        firebaseIdToken: data.firebaseIdToken || "",
      });
      setPhase("pending_approval");
      return;
    }
    const id = data?.identity || loadTsIdentity();
    const freshLogin = consumeTsFreshLogin();
    if (isDesignsOnlyIdentity(id)) {
      finalizeTsDepartments(id, id?.available_departments || ["gallery"]);
      fetchTsBootstrap()
        .catch(() => null)
        .finally(() => {
          if (typeof window !== "undefined") {
            const galleryPath = tsAppPortionPaths().gallery || "/ts/app/gallery";
            if (!window.location.pathname.includes("gallery") && !window.location.pathname.includes("designs")) {
              navigate(galleryPath, { replace: true });
            }
          }
          finishReady(id, data);
        });
      return;
    }
    if (shouldShowTsOnboarding(id, freshLogin)) {
      setIdentity(id);
      setSessionData(data);
      setPhase("needs_onboarding");
      return;
    }
    finalizeTsDepartments(id, id?.available_departments);
    fetchTsBootstrap()
      .catch(() => null)
      .finally(() => finishReady(id, data));
  };

  const handleSignupComplete = (data) => {
    setPendingEmployee(data?.employee || null);
    setPhase("pending_approval");
  };

  const handlePendingRefresh = async () => {
    const token = signupContext?.firebaseIdToken;
    if (!token) {
      goNeedsOtp("Sign in with SMS again, then check approval.");
      return;
    }
    setPendingBusy(true);
    setErr("");
    try {
      const data = await loginTsFirebaseWithStatus(token);
      if (data?.status === "pending_approval") {
        setPendingEmployee(data.employee || pendingEmployee);
        return;
      }
      if (data?.status === "ready") {
        applyTsPortalTokens(data);
        markTsFreshLogin();
        const id = data?.identity || loadTsIdentity();
        finalizeTsDepartments(id, id?.available_departments);
        await fetchTsBootstrap().catch(() => null);
        finishReady(id, data);
      }
    } catch (ex) {
      if (isRiderUseDaError(ex.message)) {
        setPhase("rider_blocked");
        return;
      }
      if (isPhoneVerificationExpired(ex)) {
        goNeedsOtp("Your verification expired. Sign in with SMS again, then check approval.");
        return;
      }
      setErr(ex.message || "Still waiting for manager approval");
    } finally {
      setPendingBusy(false);
    }
  };

  const handleOnboardingComplete = async ({ name, departments }) => {
    if (!identity) return;
    setOnboardBusy(true);
    setErr("");
    try {
      const depts =
        identity.is_ts_admin || (identity.available_departments || []).length <= 1
          ? identity.available_departments || []
          : departments;
      completeTsOnboarding(identity, { name, departments: depts });
      const updated = { ...identity, name: (name || identity.name || "").trim() };
      setIdentity(updated);
      await fetchTsBootstrap().catch(() => null);
      finishReady(updated, sessionData);
    } catch (ex) {
      setErr(ex.message || "Could not save setup");
    } finally {
      setOnboardBusy(false);
    }
  };

  if (phase === "checking") {
    return (
      <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F5F2EA" }}>
        <Loader2 className="animate-spin" size={28} color={PINK} />
      </div>
    );
  }

  if (phase === "rider_blocked") {
    return (
      <div
        style={{
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
          background: "#F5F2EA",
          padding: 20,
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div style={{ width: "100%", maxWidth: 380, textAlign: "center", background: "#fff", borderRadius: 18, padding: 22 }}>
          <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(45,45,45,0.45)", margin: "0 0 4px" }}>
            {staffBrandKicker()}
          </p>
          <p style={{ fontSize: 28, fontWeight: 900, color: PINK, margin: "0 0 16px" }}>{staffBrandTitle()}</p>
          <p style={{ color: "#2D2D2D", fontWeight: 800, marginBottom: 8 }}>Use the Delivery app</p>
          <p style={{ color: "#666", fontSize: 14, marginBottom: 18, lineHeight: 1.45 }}>
            This number is a delivery rider. Staff login is for internal team only — open the Delivery app to run your route.
          </p>
          <Link
            to={deliveryJoinPath()}
            style={{
              display: "inline-block",
              padding: "12px 18px",
              borderRadius: 14,
              background: PINK,
              color: "#fff",
              fontSize: 15,
              fontWeight: 800,
              textDecoration: "none",
              marginBottom: 12,
            }}
          >
            Open Delivery app
          </Link>
          <button
            type="button"
            onClick={() => goNeedsOtp()}
            style={{
              display: "block",
              width: "100%",
              marginTop: 8,
              border: 0,
              background: "transparent",
              color: "rgba(45,45,45,0.55)",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              padding: 8,
            }}
          >
            Use a different number
          </button>
        </div>
      </div>
    );
  }

  if (phase === "needs_otp") {
    return (
      <>
        {err ? (
          <p style={{ textAlign: "center", color: "#B91C1C", fontWeight: 700, padding: "8px 14px" }}>{err}</p>
        ) : null}
        <TsAuthGate onComplete={handleOtpComplete} />
      </>
    );
  }

  if (phase === "resume_error") {
    return (
      <div style={{ minHeight: "100dvh", display: "grid", placeItems: "center", background: "#F5F2EA", padding: 20 }}>
        <div style={{ width: "100%", maxWidth: 380, textAlign: "center", background: "#fff", borderRadius: 18, padding: 22 }}>
          <p style={{ color: "#2D2D2D", fontWeight: 800, marginBottom: 8 }}>Your login is still saved</p>
          <p style={{ color: "#666", fontSize: 14, marginBottom: 16 }}>{err || "Could not reach the server."}</p>
          <button type="button" onClick={runResume} style={{ border: 0, borderRadius: 12, padding: "11px 18px", background: PINK, color: "#fff", fontWeight: 800 }}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (phase === "needs_signup" && signupContext?.firebaseIdToken) {
    return (
      <>
        {err ? (
          <p style={{ textAlign: "center", color: "#B91C1C", fontWeight: 700, padding: "8px 14px" }}>{err}</p>
        ) : null}
        <TsEmployeeSignup
          initialPhone={signupContext.phone}
          phoneLocked
          firebaseIdToken={signupContext.firebaseIdToken}
          onComplete={handleSignupComplete}
          onCancel={() => goNeedsOtp()}
        />
      </>
    );
  }

  if (phase === "pending_approval") {
    return (
      <>
        {err ? (
          <p style={{ textAlign: "center", color: "#B91C1C", fontWeight: 700, padding: "8px 14px" }}>{err}</p>
        ) : null}
        <TsPendingApproval
          employee={pendingEmployee}
          onRefresh={handlePendingRefresh}
          onReauth={() => goNeedsOtp("Sign in with SMS again, then check approval.")}
          busy={pendingBusy}
        />
      </>
    );
  }

  if (phase === "needs_onboarding" && identity) {
    const saved = loadTsDepartments();
    const initialDepts = saved.length ? saved : identity.available_departments || [];
    return (
      <>
        {err ? (
          <p style={{ textAlign: "center", color: "#B91C1C", fontWeight: 700, padding: "8px 14px" }}>{err}</p>
        ) : null}
        <TsOnboarding
          identity={identity}
          initialDepartments={initialDepts}
          onComplete={handleOnboardingComplete}
          busy={onboardBusy}
        />
      </>
    );
  }

  if (phase === "ready") {
    return typeof children === "function" ? children({ identity, sessionData }) : children;
  }

  return null;
}
